import { ExternalLink, History, Loader2, Upload } from 'lucide-react';

function formatPublishedAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const platformAccent = {
  youtube: '#FF0000',
  instagram: '#E1306C',
};

/**
 * @param {{
 *   platform: 'youtube' | 'instagram',
 *   items: Array<{ id: string, title: string, url?: string, created_at: string, metadata?: object }>,
 *   loading?: boolean,
 *   className?: string,
 * }} props
 */
export function SocialPublishHistoryPanel({ platform, items, loading = false, className = '' }) {
  const label = platform === 'youtube' ? 'YouTube' : 'Instagram';
  const accent = platformAccent[platform];

  return (
    <aside
      className={`flex flex-col rounded-[18px] bg-white/[0.04] min-h-[280px] ${className}`.trim()}
      data-testid={`social-publish-history-${platform}`}
    >
      <div className="px-4 py-4 shrink-0">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 shrink-0" style={{ color: accent }} />
          <h2 className="text-sm font-semibold tracking-tight truncate text-white">
            Recent on {label}
          </h2>
        </div>
        <p className="text-xs text-[#8b95a7] mt-1">
          Links from uploads you send here
        </p>
      </div>

      <div className="flex-1 min-h-0 flex flex-col px-4 pb-4">
        {loading ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-[#8b95a7]">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center px-4 py-8 rounded-xl border border-dashed border-white/10 text-[#8b95a7]">
            <Upload className="w-8 h-8 mb-3 opacity-40" style={{ color: accent }} />
            <p className="text-sm font-medium text-white">
              Nothing published yet
            </p>
            <p className="text-xs mt-1 max-w-[16rem] leading-relaxed">
              After you publish, the link shows up here.
            </p>
          </div>
        ) : (
          <ul className="flex-1 min-h-0 overflow-y-auto space-y-2">
            {items.map((item) => (
              <li
                key={item.id}
                className="px-3 py-2.5 rounded-xl bg-[#0c1018] transition-colors hover:bg-white/[0.06]"
                data-testid={`social-publish-history-item-${item.id}`}
              >
                <p
                  className="text-sm font-medium line-clamp-2 leading-snug text-white"
                  title={item.title}
                >
                  {item.title}
                </p>
                <p className="text-[11px] mt-1 tabular-nums text-[#8b95a7]">
                  {formatPublishedAt(item.created_at)}
                  {item.metadata?.privacy_status ? (
                    <span className="ml-1.5 uppercase tracking-wide">· {item.metadata.privacy_status}</span>
                  ) : null}
                </p>
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs mt-1.5 hover:underline font-medium text-[#34d399]"
                    data-testid={`social-publish-history-link-${item.id}`}
                  >
                    Open on {label}
                    <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                ) : (
                  <p className="text-[11px] mt-1.5 italic text-[#8b95a7]">
                    Link not available yet
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
